import type { BuildState, SpineRecommendation } from '@/types';
import { getShaft, getInsertWeight } from './calculations';

// ============================================================
// Spine selection — recalibrated 2026-06-11 against primary-source
// Easton 301055-A + Victory spine-chart grids (Sal sign-off).
// Full validation: main repo docs/SPINE-CHART-VALIDATION-2026-06-11.md
// Harness: main repo scripts/spine-chart-validation.mjs (keep in lock-step).
// ============================================================

/**
 * Effective draw weight + recommended spine range using the standard
 * archery-industry spine selection methodology, with coefficients
 * calibrated cell-by-cell against the Easton hunting chart (301055-A)
 * and Victory's own spine-chart grids (see main repo
 * docs/SPINE-CHART-VALIDATION-2026-06-11.md — shipped 2026-06-11).
 *
 * Step 1 — start with measured peak draw weight (lbs).
 *
 * Step 2 — bow speed (ATA/IBO) adjustment — Easton and Victory publish the
 * identical table; we adopt it verbatim (compound only):
 *   IBO ≤ 300 fps:   −5  (slow cam, gentler launch)
 *   IBO 301–340:      0  (chart baseline — most modern hunting bows)
 *   IBO 341–350:     +5
 *   IBO > 350:      +10  (speed bows)
 *   traditional (longbow / recurve): −5  (longer power stroke, gentler)
 *
 * Step 3 — arrow length. Easton's chart diagonal moves one spine group per
 * ~3", i.e. ≈3.5 lb effective per inch from the 28" baseline ("BOP" — Back
 * of Point convention).
 *
 *   eff_dw += (arrowLength − 28") × 3.5
 *
 * Step 4 — front-side mass. Easton: ±3 lb per 25 gr of POINT weight from a
 * 100 gr baseline (0.12 lb/gr). Inserts up to 25 gr are free — the charts
 * already assume a standard insert is installed; only insert/collar mass
 * above 25 gr counts (also at 0.12 lb/gr). Extended front components
 * (v140 addedMassGr) count as insert-side mass and share the same 25 gr
 * allowance.
 *
 *   eff_dw += (pointWeight − 100) × 0.12
 *   eff_dw += max(0, insertWeight + componentMass − 25) × 0.12
 *
 * Step 5 — let-off (compound only). Lower let-off stores more energy in the
 * limbs at full draw = arrow sees a harsher launch = needs stiffer spine.
 * Reference: 85% is the modern hunting standard. Each 10% below adds ~2.5 lb.
 *
 *   eff_dw += (85 − letOff) × 0.25
 *
 * Step 6 — map effective draw weight to spine class. Easton's 2024 chart
 * gives a single spine per band; we expose a tight ±1 class range to cover
 * shaft-to-shaft variation across brands. The wider "anything works" range
 * used by older charts is misleading — bows that close to a band's edge
 * benefit from re-checking with a paper-tune.
 *
 *    < 25 lb  →  600–700 spine
 *    25–35    →  500–600
 *    35–45    →  400–500
 *    45–55    →  340–400   (sweet spot for 45-lb single-cam recurves)
 *    55–65    →  340–400   (typical 60-65 lb compound hunter)
 *    65–75    →  300–340
 *    75–85    →  250–300
 *    85+      →  200–250
 *
 * Sources: Easton "Hunting Arrow Size Selection" chart 301055-A (2023, PDF
 * text layer); Victory Spine Chart Calculator grids (verbatim from Victory's
 * own JS); Victory 2024 printed-chart footnotes; Gold Tip compound chart
 * (OCR, used as cross-check only — GT is the weak outlier, see validation
 * doc §7). Headline parity: Easton 98% match, Victory chart2 87% match / 3%
 * contradicted, anchor build (70#/29"/125+50gr/RIP TKO 300) passes.
 */
export function recommendSpine(state: BuildState): SpineRecommendation {
  return recommendSpineFromInputs({
    drawWeight: state.bow.drawWeight,
    bowType: state.bow.type,
    iboSpeed: state.bow.iboSpeed,
    letOff: state.bow.letOff,
    arrowLengthIn: state.length,
    pointGr: state.pointWeight,
    insertGr: getInsertWeight(state),
    selectedSpine: state.spineKey,
  });
}

/**
 * v135.1 — Pure-function variant of `recommendSpine` that accepts a plain
 * inputs object instead of a `BuildState`. The Spine Calculator view uses
 * this so the verdict matches Build Review's verdict for the same effective
 * inputs (Issue B fix from Jesse's tester report).
 *
 * Tolerance band: selected spine within ±25 of the recommended range is
 * still verdict 'ok'. Real-world tuning regularly succeeds at one spine
 * class off the chart; calling it "TOO WEAK" or "TOO STIFF" at the boundary
 * was over-aggressive (Issue C). The borderline message tells the user
 * they're inside the looser tolerance.
 */
export type FrontComponentKind =
  | 'standard'         // Standard HIT insert (~1" embed), brass insert, half-out — mass only
  | 'iron-will-snyder' // Iron Will Snyder Core (3" embed) — mass + stiffening
  | 'firenock-cti-3'   // Firenock CTI 3" — mass + stiffening
  | 'firenock-cti-6'   // Firenock CTI 6" (~46 gr) — mass + stiffening
  | 'evolution-glue-in'// Evolution Outdoors deep glue-in (~1.5" est) — mass + mild stiffening
  | 'easton-dg';       // Easton Deep Glue-in (~2" embed) — mass + stiffening

/** v140 — Front-component spec table.
 *  Models partial-shaft stiffening per docs/firenock-cti-spine-research.md
 *  and docs/iron-will-evolution-spine-research.md. */
const FRONT_COMPONENT_SPECS: Record<FrontComponentKind, { embedIn: number; addedMassGr: number }> = {
  'standard':         { embedIn: 1.0, addedMassGr: 0 },
  'iron-will-snyder': { embedIn: 3.0, addedMassGr: 30 },
  'firenock-cti-3':   { embedIn: 3.0, addedMassGr: 25 },
  'firenock-cti-6':   { embedIn: 6.0, addedMassGr: 46 },
  'evolution-glue-in':{ embedIn: 1.5, addedMassGr: 15 },
  'easton-dg':        { embedIn: 2.0, addedMassGr: 25 },
};

/** v140 stiffening damping — anchored to Ethics Archery's claim that
 *  3" embedded footer ≈ 1 spine class shift.
 *  (audit) Only referenced by the unfinished cubic-stiffening shift below,
 *  which is currently not applied; kept for when that adjustment is wired in. */
// const STIFFENING_DAMPING = 0.33;

/** v140 effective-DW shift per inch of footing beyond 1" HIT baseline.
 *  Anchored to Stu Miller's calculator (industry standard since 2009). */
const EFF_DW_PER_EXTRA_INCH = 4.0;
const EFF_DW_CAP = 12.0;

export interface SpineFromInputs {
  drawWeight: number;
  bowType: 'compound' | 'recurve' | 'traditional';
  iboSpeed?: number;
  letOff?: number;
  /** Arrow length BOP (back of point) in inches — same convention as Easton chart. */
  arrowLengthIn: number;
  pointGr: number;
  /** Insert + collar combined. */
  insertGr: number;
  /** What the archer has selected so we can render the verdict. */
  selectedSpine: number;
  /** v140 — extended front component kind. Models partial-shaft stiffening. */
  frontComponent?: FrontComponentKind;
}

/** v135.1 — Tolerance band for the verdict at boundaries. */
const SPINE_VERDICT_TOLERANCE = 25;

export function recommendSpineFromInputs(i: SpineFromInputs): SpineRecommendation {
  /* v140 — fold extended-front-component mass into total front mass, AND
   * apply an effective-DW stiffening offset for embed depth beyond 1" HIT. */
  const fc = FRONT_COMPONENT_SPECS[i.frontComponent ?? 'standard'];
  const extraInches = Math.max(0, fc.embedIn - 1.0);
  const stiffeningEffDwShift = Math.min(EFF_DW_CAP, extraInches * EFF_DW_PER_EXTRA_INCH);
  /* Component-induced cubic stiffening on the spine ladder (Firenock-flavored
   * physics: shortened oscillation radius cubes with the working-length
   * ratio). Applied as a direct spine-number shift, scaled by STIFFENING_DAMPING. */
  // NOTE (formula audit): a cubic-stiffening spine adjustment was computed here
  // but never applied to the returned recommendation. Preserved as a comment so
  // the math isn't lost; revisit whether it should be applied:
  //   const workingLenRatio = Math.max(0.5, (i.arrowLengthIn - fc.embedIn) / i.arrowLengthIn);
  //   cubicStiffeningSpineShift =
  //     i.selectedSpine * (1 - Math.pow(workingLenRatio, 3)) * STIFFENING_DAMPING;

  const totalFrontMass = i.pointGr + i.insertGr + fc.addedMassGr;

  // 2026-06-11 recalibration (Sal sign-off) — coefficients validated against
  // Easton 301055-A + Victory's own grids. See main repo
  // docs/SPINE-CHART-VALIDATION-2026-06-11.md; keep main repo
  // scripts/spine-chart-validation.mjs in lock-step with this block.
  let eff = i.drawWeight;
  eff += (i.arrowLengthIn - 28) * 3.5; // Easton diagonal ≈ 1 spine group / 3"
  eff += (i.pointGr - 100) * 0.12; // Easton: 3 lb per 25 gr of point
  // First 25 gr of insert-side mass free (charts assume a standard insert);
  // v140 extended-component mass counts as insert-side mass.
  eff += Math.max(0, i.insertGr + fc.addedMassGr - 25) * 0.12;
  /* v140 — subtract effective DW for extended-shank stiffening. */
  eff -= stiffeningEffDwShift;

  if (i.bowType === 'traditional') eff -= 5;

  if (i.bowType === 'compound') {
    // Easton/Victory bow-speed table, adopted verbatim.
    const ibo = i.iboSpeed ?? 320;
    if (ibo <= 300) eff -= 5;
    else if (ibo > 350) eff += 10;
    else if (ibo >= 341) eff += 5;
    // 301–340 fps: chart baseline, no adjustment.
    const letOff = i.letOff ?? 80;
    eff += (85 - letOff) * 0.25;
  }

  const range = effectiveDwToSpineRange(eff);
  const selected = i.selectedSpine;

  let status: SpineRecommendation['status'] = 'ok';
  let message = buildOkMessage(eff, range, totalFrontMass, i.insertGr);

  // v135.1 — borderline tolerance. Real-world tuning succeeds at one spine
  // class off the chart frequently. We only flag stiff/weak past the buffer.
  const stiffBoundary = range.min - SPINE_VERDICT_TOLERANCE;
  const weakBoundary = range.max + SPINE_VERDICT_TOLERANCE;

  if (selected < stiffBoundary) {
    status = 'stiff';
    message = `Spine ${selected} is too STIFF. Effective ${eff.toFixed(0)} lb (incl. ${totalFrontMass} gr front mass) calls for ${range.min}–${range.max} spine. Stiffer-than-ideal arrows hit LEFT of impact (RH shooter) and fly slower.`;
  } else if (selected > weakBoundary) {
    status = 'weak';
    message = `Spine ${selected} is too WEAK. Effective ${eff.toFixed(0)} lb (incl. ${totalFrontMass} gr front mass) calls for ${range.min}–${range.max} spine. Weaker-than-ideal arrows hit RIGHT of impact (RH shooter), flex unsafely, and can paradox into the riser.`;
  } else if (selected < range.min) {
    // Inside the tolerance buffer on the stiff side
    message = `Spine ${selected} is borderline — slightly stiffer than the ${range.min}–${range.max} chart range at ${eff.toFixed(0)} lb effective, but well within real-world tuning tolerance. Most setups in this band paper-tune cleanly.`;
  } else if (selected > range.max) {
    // Inside the tolerance buffer on the weak side
    message = `Spine ${selected} is borderline — slightly weaker than the ${range.min}–${range.max} chart range at ${eff.toFixed(0)} lb effective, but well within real-world tuning tolerance. Most setups in this band paper-tune cleanly.`;
  }

  return {
    effectiveDrawWeight: eff,
    recommendedMin: range.min,
    recommendedMax: range.max,
    status,
    message,
  };
}

interface SpineRange { min: number; max: number; }

function effectiveDwToSpineRange(eff: number): SpineRange {
  if (eff < 25) return { min: 600, max: 700 };
  if (eff < 35) return { min: 500, max: 600 };
  if (eff < 45) return { min: 400, max: 500 };
  if (eff < 55) return { min: 340, max: 400 };
  if (eff < 65) return { min: 340, max: 400 };
  if (eff < 75) return { min: 300, max: 340 };
  if (eff < 85) return { min: 250, max: 300 };
  return { min: 200, max: 250 };
}

/**
 * Compose the "everything checks out" message. We surface the front-mass
 * breakdown when an insert is contributing because new builders forget to
 * count heavy inserts toward spine math (a 50gr insert + 100gr field point
 * acts like a 150gr point for spine purposes).
 */
function buildOkMessage(
  eff: number,
  range: SpineRange,
  totalFrontMass: number,
  insertWeight: number,
): string {
  const base = `Effective ${eff.toFixed(0)} lb → ${range.min}–${range.max} spine.`;
  if (insertWeight > 0) {
    return `${base} (Front mass: ${totalFrontMass} gr = point + ${insertWeight} gr insert.)`;
  }
  return base;
}

/**
 * Find the spine value in this shaft's catalog closest to the recommendation
 * midpoint. Useful for a "pick the right spine for me" affordance.
 */
export function suggestSpineForState(state: BuildState): number | null {
  const shaft = getShaft(state);
  const rec = recommendSpine(state);
  const target = (rec.recommendedMin + rec.recommendedMax) / 2;
  const spines = Object.keys(shaft.spines).map(Number);
  if (spines.length === 0) return null;

  spines.sort((a, b) => Math.abs(a - target) - Math.abs(b - target));
  return spines[0]!;
}
